import {
  RhButton,
  RhCard,
  RhDivider,
  RhIcon,
  RhTooltip,
} from "@rhythm-ui/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useMemo } from "react";

import { JobType } from "@/types/job";
import { SkillType } from "@/types/utils";

import formatExperienceRange from "@/helpers/formatExperienceRange";

// import { LAKH_CURRENCY_MULTIPLIER } from "@/constants";

dayjs.extend(relativeTime);

const JobCard = ({
  job,
  onJobCardClick,
  setOpenJobApplicationStatusDrawer,
  className,
}: {
  job: JobType;
  onJobCardClick: (candidateId: number) => void;
  setOpenJobApplicationStatusDrawer: (job: JobType) => void;
  className: string;
}) => {
  const candidateWorkTypePreference = useMemo(() => {
    return (
      <>
        {job.work_preference_new?.map((item, idx, arr) => (
          <React.Fragment key={idx}>
            <p>{item}</p>
            {arr.length - 1 !== idx && (
              <p className="flex items-center">
                <RhIcon className="icon-light" icon={"ci:dot-03-m"}></RhIcon>
              </p>
            )}
          </React.Fragment>
        )) ?? "-"}
      </>
    );
  }, [job.work_preference_new]);

  const candidateLocationPreference = useMemo(
    () => "(" + job.location_new.map((city) => city.name).join(", ") + ")",

    [job.location_new],
  );

  const createdAtDate = dayjs(job.created_ts);
  const postedAt = createdAtDate.fromNow();

  const len = job.is_applied ? job?.self_applied_candidates?.length : undefined;
  const AppliedAtDate =
    len && job.is_applied
      ? dayjs(job?.self_applied_candidates[len - 1]?.is_applied_on)
      : undefined;
  const AppliedAt = AppliedAtDate?.fromNow();

  return (
    <RhCard
      className={`card-hover relative mb-tsm overflow-visible rounded ${className}`}
      data-testid="job-card"
      onClick={() => job.id && onJobCardClick(job.id)}
    >
      <div className="flex space-y-tnano px-tsm py-tm1">
        <div className="mr-tm2 flex h-[88px] w-[88px] items-center justify-between rounded-md border px-tnano">
          {job.company?.logo && (
            <img src={job.company?.logo} className="w-full" />
          )}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="mb-[6px] text-base font-medium">{job.title}</h3>
            {job.status === "Hold" && (
              <span className="text-gray rounded-md bg-yellow-50 px-2 py-0.5 text-xs">
                Job On Hold
              </span>
            )}
          </div>

          {job.company?.name && (
            <p className="text-light mb-tsm flex items-center gap-tnano">
              {job.company?.name}
            </p>
          )}
        </div>
      </div>
      <JobPositionAndSkills job={job} />
      {/* experience */}
      <div className="flex flex-col gap-tsm px-tsm pt-tm1">
        <div className="flex items-center gap-tnano">
          <RhIcon icon={"bx:briefcase-alt"} className="text-xl"></RhIcon>
          <p>
            {formatExperienceRange.number({
              min: job.min_exp,
              max: job.max_exp,
            })}
          </p>
        </div>

        {/* CTC range */}
        {/* <div className="flex items-center gap-tnano">
          <RhIcon icon={"tabler:cash-banknote"} className="text-xl"></RhIcon>
          <p>{ctcRange}</p>
        </div> */}

        {/* Location Info */}
        <div className="flex items-center gap-tnano">
          <RhIcon icon={"ph:map-pin"} className="text-xl"></RhIcon>

          <div className="flex items-center gap-tnano">
            {candidateWorkTypePreference}
          </div>
          <p className="text-light">
            {job.work_preference_new && job.location_new?.length > 0 && (
              <>{candidateLocationPreference}</>
            )}
          </p>
        </div>
      </div>
      <div className="mt-tm2">
        <RhDivider />
      </div>

      <div className="flex items-center justify-between px-tsm py-tmini">
        <div>
          <p className="text-[#7B7B7B]">
            {job.is_applied ? `Applied ${AppliedAt}` : `Posted ${postedAt}`}
          </p>
        </div>
        <div>
          <div className="flex justify-end gap-tm2">
            <RhButton layout="outline">View details</RhButton>
            {/* TODO: will need it again */}
            <RhButton
              layout="outline"
              className={job.is_applied ? "" : "hidden"}
              onClick={(e) => {
                e.stopPropagation();
                setOpenJobApplicationStatusDrawer(job);
              }}
            >
              View timeline
            </RhButton>
            {job.is_applied && (
              <RhButton layout="outline">Revoke application</RhButton>
            )}
          </div>
        </div>
      </div>
    </RhCard>
  );
};
export default JobCard;

const JobPositionAndSkills = ({ job }: { job: JobType }) => {
  if (!job?.job_role && !job.primary_skills.length) return null;

  const LIMIT_VIEW_SKILLS_TO = 3;

  const firstNSkills = useMemo(() => {
    const skills = job.primary_skills.slice(0, LIMIT_VIEW_SKILLS_TO);
    return (
      <>
        {skills.map((skills_details: SkillType, idx: number, arr) => {
          return (
            <React.Fragment key={idx}>
              <p>{skills_details.name}</p>
              {arr.length - 1 !== idx && (
                <p className="flex items-center">
                  <RhIcon className="icon-light" icon={"ci:dot-03-m"}></RhIcon>
                </p>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  }, [job.primary_skills]);

  const nextNSkills = useMemo(() => {
    const skills = job.primary_skills.slice(
      LIMIT_VIEW_SKILLS_TO,
      job.primary_skills.length,
    );
    const tootipTitle = skills.map((skill) => skill.name).join(", ");

    return (
      <>
        {tootipTitle.length > 0 && (
          <>
            <p className="flex items-center">
              <RhIcon className="icon-light" icon={"ci:dot-03-m"}></RhIcon>
            </p>
            <RhTooltip title={tootipTitle} position="bottom">
              <p>+{skills.length}</p>
            </RhTooltip>
          </>
        )}
      </>
    );
  }, [job.other_skills]);

  return (
    <div className="flex px-tsm">
      <h4>{job?.job_role?.name}</h4>
      {job?.job_role?.name && job.primary_skills.length > 0 && (
        <span className="text-hint mx-tm1">|</span>
      )}
      <div className="flex items-center gap-tmini">
        {firstNSkills}
        {nextNSkills}
      </div>
    </div>
  );
};
